import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useGoldPrice, calcProfit } from '@/hooks/useGoldPrice';

interface OpenOrder {
  name: string;
  direction: string;
  weight: number;
  openPrice: number;
}

export interface LiveAssets {
  loading: boolean;
  /** 可用余额（可用定金） */
  available: number;
  /** 已用余额（已用定金，占用在持仓里） */
  used: number;
  /** 实时浮动盈亏 */
  floatingProfit: number;
  /** 总资产 = 可用 + 已用 + 浮动盈亏 + 全款实物市值 */
  totalAssets: number;
  /** 浮动盈亏收益率 */
  profitPercent: number;
  longWeight: number;
  shortWeight: number;
  netWeight: number;
  /** 持有黄金克重（净持仓） */
  holdingGold: number;
  /** 实时金价 */
  goldPrice: number;
  /** 全款实物黄金克重（净持仓扣除 open 定金持仓） */
  physicalWeight: number;
  /** 全款实物黄金实时市值 */
  physicalValue: number;
}

/**
 * 实时账户资产 Hook
 * - 从 account_summary 读取可用/已用余额与持仓克重
 * - 从 open 订单计算实时浮动盈亏（统一使用回购价 bidPrice，与订单页面一致）
 * - 金价 tick 时自动刷新，供行情页 / 我的页复用
 */
export function useLiveAssets(): LiveAssets {
  const { getBidPrice, getPrice, isPriceLive } = useGoldPrice();
  const [summary, setSummary] = useState({
    available: 0,
    used: 0,
    longWeight: 0,
    shortWeight: 0,
    netWeight: 0,
  });
  const [openOrders, setOpenOrders] = useState<OpenOrder[]>([]);
  const [loading, setLoading] = useState(true);

  const [goldAssets, setGoldAssets] = useState<{ weight: number }[]>([]);

  const fetchData = useCallback(async () => {
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const userId = sessionData.session?.user?.id;
      if (!userId) {
        setLoading(false);
        return;
      }

      const [summaryRes, ordersRes, goldRes] = await Promise.all([
        supabase.from('account_summary').select('*').eq('user_id', userId).maybeSingle(),
        supabase
          .from('orders')
          .select('name, direction, weight, open_price')
          .eq('status', 'open')
          .eq('user_id', userId),
        supabase.from('gold_assets').select('weight, status').eq('user_id', userId).neq('status', '已回购').neq('status', '已提取'),
      ]);

      if (summaryRes.error) throw summaryRes.error;
      if (ordersRes.error) throw ordersRes.error;

      if (summaryRes.data) {
        setSummary({
          available: Number(summaryRes.data.available_margin) || 0,
          used: Number(summaryRes.data.used_margin) || 0,
          longWeight: Number(summaryRes.data.long_weight) || 0,
          shortWeight: Number(summaryRes.data.short_weight) || 0,
          netWeight: Number(summaryRes.data.net_weight) || 0,
        });
      } else {
        setSummary({
          available: 0,
          used: 0,
          longWeight: 0,
          shortWeight: 0,
          netWeight: 0,
        });
      }

      setOpenOrders(
        (ordersRes.data || []).map((o) => ({
          name: o.name,
          direction: o.direction,
          weight: Number(o.weight) || 0,
          openPrice: Number(o.open_price) || 0,
        })),
      );

      setGoldAssets(
        (goldRes.data || []).map((g) => ({ weight: Number(g.weight) || 0 })),
      );
    } catch {
      // 保留默认值
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // 兜底：10秒后强制结束 loading，避免网络或查询卡住导致总资产永远转圈
  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 10000);
    return () => clearTimeout(timer);
  }, []);

  // 实时浮动盈亏：随金价 tick 自动重算
  // 做空(OUT)按销售价(ask)计算，做多(IN)按回购价(bid)计算
  let floatingProfit = 0;
  for (const o of openOrders) {
    // 该品种无真实实时行情时不计算浮盈，避免用兑底/占位价（如 954）算出错误盈亏
    if (!isPriceLive(o.name)) continue;
    const livePrice = o.direction === 'OUT'
      ? (getPrice(o.name) || o.openPrice)
      : (getBidPrice(o.name) || o.openPrice);
    floatingProfit += calcProfit(o.direction, o.weight, o.openPrice, livePrice);
  }

  // 实物金市值也只认真实行情，避免断线时用占位价虚增/虚减总资产
  let goldPrice = 0;
  if (isPriceLive('Au9999')) goldPrice = getBidPrice('Au9999');
  else if (isPriceLive('黄金')) goldPrice = getBidPrice('黄金');
  else if (isPriceLive('金条价')) goldPrice = getBidPrice('金条价');
  const physicalWeight = goldAssets.reduce((sum, g) => sum + g.weight, 0);
  const physicalValue = physicalWeight * goldPrice;

  const baseAssets = summary.available + summary.used;
  const totalAssets = baseAssets + floatingProfit + physicalValue;
  const profitPercent = baseAssets > 0 ? (floatingProfit / baseAssets) * 100 : 0;

  return {
    loading,
    available: summary.available,
    used: summary.used,
    floatingProfit,
    totalAssets,
    profitPercent,
    longWeight: summary.longWeight,
    shortWeight: summary.shortWeight,
    netWeight: summary.netWeight,
    holdingGold: physicalWeight,
    goldPrice,
    physicalWeight,
    physicalValue,
  };
}